/**
 * Episode plan (2.5.1–2.5.4) on the patient page: parsed frequencies as
 * per-discipline tracks on SOC-anchored seven-day weeks across the cert period;
 * rule-generated visits (supervisory q14d, recert days 56–60, ROC ≤48h); the
 * payer gate — visits born real when clear, hatched beyond authorized units with
 * the ledger firing the predictive re-auth first; plan versions with an interim
 * overlay, never a rebuild. Say "episode plan" in the room, not "contract".
 */
import { useMemo } from "react";
import { reservedLabel } from "../../engine/reserved";
import { DONE, RESERVED, SCHEDULED } from "./visitStatusColors";
import { Box, Chip, Stack, Tooltip, Typography } from "@mui/material";
import { lineFor, selectAuthCoverage, type AuthCoverage } from "../../engine/authLedger";
import { addDaysToKey, dateKeyStart, formatInBranchZone, selectDemoNow, toDateKey } from "@caredna-marlin/utils/homeHealthDemo/engine/clock";
import { describeFrequency, formatFrequency, parseFrequency } from "@caredna-marlin/utils/homeHealthDemo/engine/frequency";
import { dayOfCert, recertWindow } from "@caredna-marlin/utils/homeHealthDemo/engine/rules";
import { visitTypeLabel } from "../../engine/roster";
import type { AuthLine, DemoState, Discipline, Episode, PlanVisit } from "@caredna-marlin/utils/homeHealthDemo/state";
import { useDemoState } from "../../store/useDemoStore";
import { useBranchClock } from "../../components/branchClock";

const DISCIPLINE_ORDER: Discipline[] = ["RN", "LPN", "PT", "PTA", "OT", "COTA", "SLP", "MSW", "HHA"];

export function selectEpisodeFor(state: Pick<DemoState, "episodes">, patientRef: string): Episode | undefined {
  const mine = Object.values(state.episodes).filter((e) => e.patientRef === patientRef);
  return mine.sort((a, b) => b.socDate.localeCompare(a.socDate))[0];
}

export function EpisodePlanSection({ patientRef }: { patientRef: string }) {
  const state = useDemoState();
  const tz = state.config.branch.timeZone;
  const clock = useBranchClock(tz);
  const patient = state.patients[patientRef];
  const episode = useMemo(() => selectEpisodeFor(state, patientRef), [state, patientRef]);
  const visits = useMemo(() => (episode ? Object.values(state.planVisits).filter((v) => v.episodeId === episode.id && v.status !== "canceled").sort((a, b) => a.targetDate.localeCompare(b.targetDate)) : []), [state.planVisits, episode]);
  const gated = patient?.payer === "MA";
  const coverage = useMemo(() => (episode ? selectAuthCoverage(episode, visits, gated) : undefined), [episode, visits, gated]);
  if (!episode || !patient) return null;

  const today = toDateKey(selectDemoNow(state, new Date()), tz);
  const weekCount = Math.ceil(state.config.certPeriodDays / 7);
  const weeks = Array.from({ length: weekCount }, (_, i) => ({ index: i, start: addDaysToKey(episode.socDate, i * 7), end: addDaysToKey(episode.socDate, Math.min(i * 7 + 6, state.config.certPeriodDays - 1)) }));
  const currentVersion = episode.versions[episode.versions.length - 1];
  const disciplines = DISCIPLINE_ORDER.filter((d) => visits.some((v) => v.discipline === d));
  const recert = recertWindow(episode);
  const dayNo = dayOfCert(episode, today);
  const fmt = (d: string, p = "MMM d") => formatInBranchZone(dateKeyStart(d, tz), tz, p);

  return (
    <Box>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
        <Typography variant="h6">Episode plan</Typography>
        <Typography variant="body2" color="text.secondary">
          SOC {fmt(episode.socDate, "EEE MMM d")} · cert period through {fmt(episode.certEndDate, "EEE MMM d")} · {dayNo >= 1 && dayNo <= state.config.certPeriodDays ? `day ${dayNo} of ${state.config.certPeriodDays}` : dayNo < 1 ? "before SOC" : "cert period ended"} · {patient.payer === "MA" ? "Medicare Advantage" : "Medicare FFS"}
        </Typography>
      </Stack>

      {/* orders → tracks (2.5.1) */}
      {currentVersion && (
        <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
          {currentVersion.orders.map((o) => {
            const parsed = safeParse(o.pattern);
            return (
              <Tooltip key={`${o.discipline}-${o.pattern}`} title={`${parsed ? formatFrequency(parsed) : o.pattern}${parsed?.hasRange ? ` — range: the plan uses the midpoint (${parsed.segments.map((s) => `${s.perWeek}/wk × ${s.weeks}`).join(", ")})` : parsed && !parsed.evalOnly ? ` — ${parsed.totalVisits} visits over ${parsed.totalWeeks} weeks` : ""}`}>
                <Chip size="small" label={`${o.discipline} ${parsed?.evalOnly ? "evaluation" : parsed ? describeFrequency(parsed) : o.pattern}${o.requiredTraits?.length ? ` · ${o.requiredTraits.join(", ")}` : ""}`} />
              </Tooltip>
            );
          })}
          {/* 2.1: a discipline that was ordered but has not planned yet — reserved, awaiting plan, on the branch read; no track here. */}
          {episode.pendingDisciplines.map((p) => (
            <Tooltip key={`pending-${p.discipline}`} title={`reserved ${p.pointsPerWeek} points a week for ${p.weeks} weeks on the branch read, awaiting plan, until the orders land`}>
              <Chip size="small" variant="outlined" label={`${p.discipline} · frequencies to follow`} sx={{ borderStyle: "dashed" }} />
            </Tooltip>
          ))}
        </Stack>
      )}

      <Box sx={{ overflowX: "auto" }}>
        <Box component="table" sx={{ borderCollapse: "collapse", minWidth: 820, width: "100%", "& th, & td": { border: "1px solid #e6e6e6", p: 0.5, fontSize: 11.5, verticalAlign: "top" }, "& th": { bgcolor: "#f3f3f3", fontWeight: 600, textAlign: "left" } }}>
          <thead>
            <tr>
              <th style={{ width: 90 }}>Track</th>
              {weeks.map((w) => {
                const isNow = today >= w.start && today <= w.end;
                const isRecert = w.end >= recert.start && w.start <= recert.end;
                return (
                  <th key={w.index} style={{ background: isNow ? "#fff4d6" : undefined }}>
                    Wk {w.index + 1}
                    <Typography component="div" sx={{ fontSize: 10, fontWeight: 400, color: "text.secondary" }}>
                      {fmt(w.start)} – {fmt(w.end)}
                      {isRecert ? " · recert window" : ""}
                    </Typography>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {disciplines.map((d) => {
              const live = visits.filter((v) => v.discipline === d);
              // **Who carries this discipline** (Pete, 2026-09-22). One name on
              // almost every case, so the row can say it; a track that has
              // changed hands names both, in the order they appear on the plan.
              const clinicians = Array.from(new Set(live.map((v) => (v.providerHandle ? state.roster[v.providerHandle]?.displayName : undefined)).filter((n): n is string => !!n)));
              return (
              <tr key={d}>
                <td>
                  <b>{d}</b>
                  {clinicians.length > 0 && (
                    <Typography component="div" sx={{ fontSize: 10, color: "text.secondary" }}>
                      {clinicians.join(", ")}
                    </Typography>
                  )}
                  <Typography component="div" sx={{ fontSize: 10, color: "text.secondary" }}>
                    {live.length} visits
                  </Typography>
                </td>
                {weeks.map((w) => (
                  <td key={w.index} style={{ background: today >= w.start && today <= w.end ? "#fffaf0" : undefined }}>
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                      {visits
                        .filter((v) => v.discipline === d && v.weekIndex === w.index)
                        .map((v) => (
                          <VisitChip key={v.id} visit={v} coverage={coverage?.byVisitId[v.id]} tz={tz} label={visitTypeLabel(state.config, v.visitTypeKind)} clinician={v.providerHandle ? state.roster[v.providerHandle]?.displayName : undefined} ledger={episode.authLedger} />
                        ))}
                    </Stack>
                  </td>
                ))}
              </tr>
              );
            })}
          </tbody>
        </Box>
      </Box>

      <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap sx={{ mt: 1, fontSize: 11.5, color: "text.secondary" }}>
        <Legend swatch={DONE} label="done" />
        <Legend swatch={SCHEDULED} label="scheduled" />
        <Legend swatch={RESERVED} label="reserved" />
        <span>S supervisory · R recert · ROC resumption of care</span>
      </Stack>

      {/* versions (2.5.4). Pete, 2026-09-22: the version a visit belonged to was
          on the chip, on the chip's border, on a legend swatch and in the
          header, and none of it was a thing to act on — so all four went. What
          is left is the fact that the plan has been revised and when, and every
          revision says the same true thing: the orders came from the EHR. */}
      {episode.versions.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2" fontWeight={600}>
            Plan versions
          </Typography>
          <Stack spacing={0.25} sx={{ mt: 0.5 }}>
            {[...episode.versions].reverse().map((v) => (
              <Typography key={v.version} variant="body2">
                <b>v{v.version}</b> · {clock.dateTime(v.createdAtIso)} · Updated from EHR
              </Typography>
            ))}
          </Stack>
        </Box>
      )}
    </Box>
  );
}

function safeParse(pattern: string) {
  try {
    return parseFrequency(pattern);
  } catch {
    return undefined;
  }
}

function VisitChip({ visit, coverage, tz, label, clinician, ledger }: { visit: PlanVisit; coverage?: AuthCoverage; tz: string; label: string; clinician?: string; ledger: AuthLine[] }) {
  const clock = useBranchClock(tz);
  const day = formatInBranchZone(dateKeyStart(visit.targetDate, tz), tz, "EEE d");
  const glyph = visit.rule === "supervisory" ? "S" : visit.rule === "recert" ? "R" : visit.rule === "roc" ? "ROC" : visit.visitTypeKind === "SOC" ? "SOC" : visit.visitTypeKind.endsWith("_EVAL") ? "E" : "";
  const style: Record<string, unknown> = {
    display: "inline-flex",
    alignItems: "center",
    gap: 0.25,
    px: 0.5,
    height: 18,
    borderRadius: 0.5,
    fontSize: 10,
    lineHeight: 1,
    color: "#333",
    border: "1px solid #ccc",
    bgcolor: "#f5f5f5",
  };
  // **Authorized reads as scheduled here** (Pete, 2026-09-22): *"I know you
  // didn't actually schedule visits out that far and that's why it's like that
  // but IRL we would schedule them."* This table is the *plan* — what the orders
  // and the payer between them say will happen — so a visit the authorization
  // covers belongs on it as a visit, whatever the seed happened to create.
  //
  // What is not covered is a **reservation**, in the same fade and the same word
  // the calendar dialog uses; the amber hatch it used to get is gone (script
  // author, discussion turn 58). Two different things arrive here as one:
  //
  //   - **after the current authorization** — no ledger line covers the date, so
  //     it waits on the re-auth for the next payment period. Whitfield's late RN
  //     visits are this, and the ledger still has units unspent.
  //   - **beyond authorized units** — a line covers the date and its units ran
  //     out. Whitfield's PT visits past the sixth are this.
  //
  // The hover says which. And a **rule-generated** assessment never takes the
  // pending-auth reason: the recert's gate is its window and the re-auth is what
  // covers it, so hatching it as beyond units would put the one visit in the
  // period that is not in doubt in doubt.
  const ruleReserved = visit.rule === "recert" ? "recertWindow" : undefined;
  const uncovered = coverage === "beyondAuth" && !ruleReserved;
  const outsideDates = uncovered && !lineFor(ledger, visit.discipline, visit.targetDate);
  if (visit.status === "completed") Object.assign(style, { ...DONE });
  else if (uncovered || ruleReserved) Object.assign(style, { ...RESERVED });
  else Object.assign(style, { ...SCHEDULED });
  const status =
    visit.status === "completed"
      ? "delivered"
      : ruleReserved
          ? reservedLabel(ruleReserved)
          : uncovered
            ? `${reservedLabel("pendingAuth")} — ${outsideDates ? "after the current authorization" : "beyond authorized units"}`
            : "scheduled";
  return (
    <Tooltip title={`${label} · ${formatInBranchZone(dateKeyStart(visit.targetDate, tz), tz, "EEE MMM d")}${visit.windowStart ? ` · ${clock.wall(visit.windowStart)}–${clock.wall(visit.windowEnd ?? visit.windowStart)}` : ""}${clinician ? ` · ${clinician}` : ""} · ${status}${visit.rule ? ` · rule: ${visit.rule}` : ""}`}>
      <Box component="span" sx={style} data-status={visit.status} data-coverage={coverage ?? ""}>
        {glyph && <b>{glyph}</b>}
        <span>{day}</span>
        {/* No check: the fill says delivered now, and it said it twice (Pete, 2026-09-22). */}
      </Box>
    </Tooltip>
  );
}

function Legend({ swatch, label }: { swatch: Record<string, unknown>; label: string }) {
  return (
    <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
      <Box component="i" sx={{ display: "inline-block", width: 14, height: 12, border: "1px solid #ccc", ...swatch }} />
      {label}
    </Box>
  );
}
